import { type AppNode, type BoundingBox } from '../types';
import { computeGroupBounds, type GroupBounds, getGroupMembers } from './group/nodeGrouping';

export type PixelSize = {
  height: number;
  width: number;
};

export type CropSession = {
  boundingBox: BoundingBox<'normalized'>;
  cropBoxes: Record<string, BoundingBox<'normalized'>>;
  cropNodeIds: string[];
  cropTargetSize: PixelSize | null;
};

export type CropBoxAnchorX = 'center' | 'left' | 'right';

export type CropBoxAnchorY = 'bottom' | 'center' | 'top';

export type CropBoxAnchor = {
  x: CropBoxAnchorX;
  y: CropBoxAnchorY;
};

export type InferCropBoxAnchorParams = {
  image: PixelSize;
  next: BoundingBox<'normalized'>;
  previous: BoundingBox<'normalized'>;
};

export type PlaceBoxAtTargetSizeParams = {
  anchor: CropBoxAnchor;
  image: PixelSize;
  previous: BoundingBox<'normalized'>;
  target: PixelSize;
};

type PixelEdges = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

export const CENTER_CROP_BOX_ANCHOR: CropBoxAnchor = { x: 'center', y: 'center' };

const IMAGE_CENTER = 0.5;

const FULL_IMAGE_BOX: BoundingBox<'normalized'> = { __unit: 'normalized', height: 1, width: 1, x: 0, y: 0 };

const roundedPixelEdges = (box: BoundingBox<'normalized'>, image: PixelSize): PixelEdges => ({
  bottom: Math.round((box.y + box.height) * image.height),
  left: Math.round(box.x * image.width),
  right: Math.round((box.x + box.width) * image.width),
  top: Math.round(box.y * image.height),
});

export const sortNodeIdsLeftToRight = (nodes: AppNode[], ids: string[]): string[] => {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return [...ids].sort((leftId, rightId) => {
    const left = nodeById.get(leftId);
    const right = nodeById.get(rightId);

    if (!left || !right) {
      return 0;
    }

    const deltaX = left.position.x - right.position.x;

    if (deltaX !== 0) {
      return deltaX;
    }

    return left.position.y - right.position.y;
  });
};

export const getNodeDisplaySize = (node: AppNode): PixelSize => ({
  height: (typeof node.style?.height === 'number' ? node.style.height : node.height) ?? 0,
  width: (typeof node.style?.width === 'number' ? node.style.width : node.width) ?? 0,
});

export const boxFromTargetSize = (
  image: PixelSize,
  target: PixelSize,
  center?: { x: number; y: number },
): BoundingBox<'normalized'> => {
  const width = image.width === 0 ? 1 : target.width / image.width;
  const height = image.height === 0 ? 1 : target.height / image.height;
  const cx = center?.x ?? IMAGE_CENTER;
  const cy = center?.y ?? IMAGE_CENTER;

  return {
    __unit: 'normalized',
    height,
    width,
    x: cx - width / 2,
    y: cy - height / 2,
  };
};

export const targetSizeFromBox = (box: BoundingBox<'normalized'>, image: PixelSize): PixelSize => ({
  height: Math.round(box.height * image.height),
  width: Math.round(box.width * image.width),
});

export const inferCropBoxAnchor = ({ image, next, previous }: InferCropBoxAnchorParams): CropBoxAnchor => {
  const previousEdges = roundedPixelEdges(previous, image);
  const nextEdges = roundedPixelEdges(next, image);
  const leftMoved = previousEdges.left !== nextEdges.left;
  const rightMoved = previousEdges.right !== nextEdges.right;
  const topMoved = previousEdges.top !== nextEdges.top;
  const bottomMoved = previousEdges.bottom !== nextEdges.bottom;

  let x: CropBoxAnchorX = 'center';

  if (leftMoved && !rightMoved) {
    x = 'right';
  } else if (rightMoved && !leftMoved) {
    x = 'left';
  }

  let y: CropBoxAnchorY = 'center';

  if (topMoved && !bottomMoved) {
    y = 'bottom';
  } else if (bottomMoved && !topMoved) {
    y = 'top';
  }

  return { x, y };
};

export const placeBoxAtTargetSize = ({
  anchor,
  image,
  previous,
  target,
}: PlaceBoxAtTargetSizeParams): BoundingBox<'normalized'> => {
  const prevLeft = previous.x * image.width;
  const prevRight = (previous.x + previous.width) * image.width;
  const prevTop = previous.y * image.height;
  const prevBottom = (previous.y + previous.height) * image.height;

  let left = (prevLeft + prevRight) / 2 - target.width / 2;

  if (anchor.x === 'left') {
    left = prevLeft;
  } else if (anchor.x === 'right') {
    left = prevRight - target.width;
  }

  let top = (prevTop + prevBottom) / 2 - target.height / 2;

  if (anchor.y === 'top') {
    top = prevTop;
  } else if (anchor.y === 'bottom') {
    top = prevBottom - target.height;
  }

  return {
    __unit: 'normalized',
    height: image.height === 0 ? 1 : target.height / image.height,
    width: image.width === 0 ? 1 : target.width / image.width,
    x: image.width === 0 ? 0 : left / image.width,
    y: image.height === 0 ? 0 : top / image.height,
  };
};

export const syncCropBoxesToTargetSize = (
  cropBoxes: Record<string, BoundingBox<'normalized'>>,
  nodeSizes: Record<string, PixelSize>,
  target: PixelSize,
  anchor: CropBoxAnchor = CENTER_CROP_BOX_ANCHOR,
): Record<string, BoundingBox<'normalized'>> => {
  const next: Record<string, BoundingBox<'normalized'>> = {};

  for (const [id, box] of Object.entries(cropBoxes)) {
    const image = nodeSizes[id];

    if (!image || image.width === 0 || image.height === 0) {
      next[id] = box;
      continue;
    }

    next[id] = placeBoxAtTargetSize({
      anchor,
      image,
      previous: box,
      target,
    });
  }

  return next;
};

export const buildCropSession = (nodes: AppNode[], cropNodeIds: string[], primaryId: string): CropSession => {
  const uniqueIds = [...new Set(cropNodeIds)];
  const primary = nodes.find((node) => node.id === primaryId);
  const primarySize = primary ? getNodeDisplaySize(primary) : { height: 0, width: 0 };
  const cropTargetSize = primarySize.width > 0 && primarySize.height > 0 ? primarySize : null;
  const cropBoxes: Record<string, BoundingBox<'normalized'>> = {};

  for (const id of uniqueIds) {
    const node = nodes.find((candidate) => candidate.id === id);

    if (!node) {
      continue;
    }

    cropBoxes[id] = cropTargetSize ? boxFromTargetSize(getNodeDisplaySize(node), cropTargetSize) : FULL_IMAGE_BOX;
  }

  return {
    boundingBox: cropBoxes[primaryId] ?? FULL_IMAGE_BOX,
    cropBoxes,
    cropNodeIds: uniqueIds,
    cropTargetSize,
  };
};

const groupTargetSize = (bounds: GroupBounds | undefined): PixelSize | null => {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }

  return { height: Math.ceil(bounds.height), width: Math.ceil(bounds.width) };
};

/** Source pixels for one group's crop frame. This stays the group's bounds, unlike `cropTargetSize`. */
export const groupFramePixelSize = (nodes: AppNode[], groupId: string): PixelSize | null =>
  groupTargetSize(computeGroupBounds(getGroupMembers(nodes, groupId)));

/** One frame over the group's union, in the same normalized space `compositeGroup` rasterizes. */
export const buildGroupCropSession = (cropNodeIds: string[], bounds: GroupBounds): CropSession => {
  return {
    boundingBox: FULL_IMAGE_BOX,
    cropBoxes: {},
    cropNodeIds: [...new Set(cropNodeIds)],
    cropTargetSize: groupTargetSize(bounds),
  };
};

export type GroupCropFrame = {
  bounds: GroupBounds;
  groupId: string;
};

/** One full frame per group. Boxes are keyed by group id, normalized to that group's bounds. */
export const buildMultiGroupCropSession = (
  cropNodeIds: string[],
  frames: GroupCropFrame[],
  focusedGroupId: string,
): CropSession => {
  const cropBoxes: Record<string, BoundingBox<'normalized'>> = {};

  for (const frame of frames) {
    cropBoxes[frame.groupId] = FULL_IMAGE_BOX;
  }

  const focused = frames.find((frame) => frame.groupId === focusedGroupId) ?? frames[0];

  return {
    boundingBox: FULL_IMAGE_BOX,
    cropBoxes,
    cropNodeIds: [...new Set(cropNodeIds)],
    cropTargetSize: focused ? groupTargetSize(focused.bounds) : null,
  };
};

export type LooseCropNode = {
  id: string;
  size: PixelSize;
};

/** Group frames plus a full frame on each image that is not in those groups. */
export const buildMixedCropSession = (
  cropNodeIds: string[],
  frames: GroupCropFrame[],
  looseNodes: LooseCropNode[],
  focusedSize: PixelSize | null,
): CropSession => {
  const cropBoxes: Record<string, BoundingBox<'normalized'>> = {};

  for (const frame of frames) {
    cropBoxes[frame.groupId] = FULL_IMAGE_BOX;
  }

  for (const looseNode of looseNodes) {
    cropBoxes[looseNode.id] = FULL_IMAGE_BOX;
  }

  return {
    boundingBox: FULL_IMAGE_BOX,
    cropBoxes,
    cropNodeIds: [...new Set(cropNodeIds)],
    cropTargetSize: focusedSize,
  };
};
